<script lang="ts" setup>
import dayjs from 'dayjs';
import { defaultLocations } from '~/constants/defaultLocations';
import { DELIVERY_SERVICES } from '~/constants/deliveryServices';
import { ONLINE_WAREHOUSE_ID } from '~/constants/warehouse';
import type { OfferBalance } from '~/types/Catalog';
import type { DeliveryServiceDetailed } from '~/types/Checkout';
import { isFreeDeliveryAvailableFromDeliveryCalculation } from '~/utils/freeDeliveryFlag';
import { getSelectedMulticardOffer } from '~/utils/multicard';

const emit = defineEmits<{
  'update:is-free-delivery-available': [value: boolean];
}>();

const catalogStore = useCatalogStore();
const { isCatalogScrollRestorationPending } = useCatalogScrollRestoration();

const isShowShopsModal = shallowRef<boolean>(false);
const isShowDeliveryTermsModal = shallowRef<boolean>(false);
const currentOfferBalances = computed(() => {
  return (
    getSelectedMulticardOffer(catalogStore.selectedOffer, catalogStore.selectedSizeId)?.offer_balances ??
    catalogStore.selectedOffer.offer_balances ??
    []
  );
});
const confirmedOfferBalances = ref<OfferBalance[]>([]);

watch(
  currentOfferBalances,
  (value) => {
    if (value.length) confirmedOfferBalances.value = value;
  },
  { immediate: true },
);

const offerBalances = computed(() => {
  if (currentOfferBalances.value.length) return currentOfferBalances.value;

  return isCatalogScrollRestorationPending.value ? confirmedOfferBalances.value : [];
});

// число всех офлайн магазинов с остатком > 0
const offlineAvailableShopsCount = computed(
  () =>
    offerBalances.value.filter(
      (balance: OfferBalance) =>
        balance.balance > 0 &&
        balance.warehouse.id !== ONLINE_WAREHOUSE_ID &&
        balance.warehouse.pickup_store !== null &&
        balance.warehouse.show,
    ).length,
);

// предыдущее число + слово "магазин" в правильном падеже и числе
const offlineShopsCountPluralized = computed(
  () =>
    `${offlineAvailableShopsCount.value} ${getNoun(offlineAvailableShopsCount.value ?? 0, 'магазина', 'магазинов', 'магазинов')}`,
);

// число магазинов, в которые можно привезти заказ для самовывоза
const shopsAvailableToPickup = computed(
  () =>
    offerBalances.value.filter(
      (balance: OfferBalance) =>
        balance.warehouse.id !== ONLINE_WAREHOUSE_ID &&
        balance.warehouse.pickup_store !== null &&
        !balance.warehouse.pickup_store.only_stock &&
        balance.warehouse.show,
    ).length,
);

// предыдущее число + слово "магазин" в правильном падеже и числе
const shopsAvailableToPickupPluralized = computed(
  () =>
    `${shopsAvailableToPickup.value} ${getNoun(shopsAvailableToPickup.value ?? 0, 'магазина', 'магазинов', 'магазинов')}`,
);

// показывать кнопку "самовывоз из Х магазинов"
const isShowSelfPickupBtn = computed(
  () =>
    offlineAvailableShopsCount.value > 0 ||
    (shopsAvailableToPickup.value > 0 && isAvailableInOnlineStore.value) ||
    (shopsAvailableToPickup.value > 0 && hasSupplierStock.value),
);

// булево, показывающее, есть ли остаток > 0 в интернет-магазине
const isAvailableInOnlineStore = computed(() => {
  const onlineStore = offerBalances.value.find(
    (balance: OfferBalance) => balance.warehouse.id === ONLINE_WAREHOUSE_ID && balance.warehouse.show,
  );

  return onlineStore ? onlineStore.balance > 0 : false;
});

// есть ли склад-поставщик с остатком (pickup_store === null, не онлайн-склад)
const hasSupplierStock = computed(() =>
  offerBalances.value.some(
    (b: OfferBalance) =>
      b.balance > 0 && b.warehouse.show && b.warehouse.pickup_store === null && b.warehouse.id !== ONLINE_WAREHOUSE_ID,
  ),
);

// есть ли склад-поставщик с delivery_available и остатком
const hasDeliveryAvailableSupplier = computed(() =>
  offerBalances.value.some(
    (b: OfferBalance) =>
      b.balance > 0 &&
      b.warehouse.show &&
      b.warehouse.delivery_available &&
      b.warehouse.pickup_store === null &&
      b.warehouse.id !== ONLINE_WAREHOUSE_ID,
  ),
);

// минимальный extra_shipping_days среди офлайн-магазинов с остатком
const minOfflineExtraShippingDays = computed(() => {
  const offlineBalances = offerBalances.value.filter(
    (b: OfferBalance) =>
      b.balance > 0 && b.warehouse.show && b.warehouse.id !== ONLINE_WAREHOUSE_ID && b.warehouse.pickup_store !== null,
  );
  return offlineBalances.length ? Math.min(...offlineBalances.map((b) => b.warehouse.extra_shipping_days)) : 0;
});

// минимальный extra_shipping_days среди источников (онлайн-склад или поставщик) с остатком
const minSourceExtraShippingDays = computed(() => {
  const sources = offerBalances.value.filter(
    (b: OfferBalance) =>
      b.balance > 0 &&
      b.warehouse.show &&
      (b.warehouse.id === ONLINE_WAREHOUSE_ID ||
        (b.warehouse.pickup_store === null && b.warehouse.id !== ONLINE_WAREHOUSE_ID)),
  );
  return sources.length ? Math.min(...sources.map((b) => b.warehouse.extra_shipping_days)) : 0;
});

function formatExtraDays(days: number): string {
  const target = dayjs().add(days, 'day');
  const today = dayjs();
  if (target.isSame(today, 'day')) return 'сегодня';
  if (target.isSame(today.add(1, 'day'), 'day')) return 'завтра';
  if (target.isSame(today.add(2, 'day'), 'day')) return 'послезавтра';
  return target.format('D MMMM');
}

// число магазинов, из которых можно забрать товар для доставки
const shopsAvailableToTakeItemForDelivery = computed(
  () =>
    offerBalances.value.filter(
      (balance: OfferBalance) =>
        balance.balance > 0 &&
        balance.warehouse.delivery_available &&
        balance.warehouse.pickup_store &&
        balance.warehouse.show,
    ).length,
);

// показывать кнопку "Узнать стоимость доставки"
const isShowDeliveryBtn = computed(
  () =>
    isAvailableInOnlineStore.value ||
    shopsAvailableToTakeItemForDelivery.value > 0 ||
    hasDeliveryAvailableSupplier.value,
);

const checkoutStore = useCheckoutStore();
const selectedDeliveryOfferId = computed(
  () => catalogStore.selectedSizeId ?? catalogStore.selectedOffer.selected_offer_id ?? catalogStore.selectedOffer.id,
);

const deliveries = ref<{ time: string; title: string; price: number | null }[]>([]);
const isLoading = ref(false);
const isDeliveryPricesLoaded = ref(false);
const availableDeliveriesDetails = ref<DeliveryServiceDetailed[]>([]);
let deliveryRequestId = 0;

function getFastestDelivery(deliveriesArray: DeliveryServiceDetailed[]) {
  return deliveriesArray.length
    ? deliveriesArray
        .filter((delivery) => delivery.delivery_period_min)
        .sort((a, b) => Number(a.delivery_period_min) - Number(b.delivery_period_min))[0]
    : null;
}

function calculateDeliveryTime(delivery: DeliveryServiceDetailed): string {
  let result: string;
  let closestDeliveryDay: dayjs.Dayjs;
  const today: dayjs.Dayjs = dayjs();

  if (delivery.slug === DELIVERY_SERVICES.russian_post.slug) {
    const fastestDelivery = getFastestDelivery(availableDeliveriesDetails.value);

    if (delivery.delivery_period_min) {
      closestDeliveryDay = dayjs().add(delivery.delivery_period_min, 'day'); // если [вдруг] у почты пришел свой расчет
    } else if (fastestDelivery?.delivery_period_min) {
      closestDeliveryDay = dayjs().add(fastestDelivery.delivery_period_min + 3, 'day'); // если у почты пусто, берем расчет самой быстрой доставки + 3 дня
    } else {
      closestDeliveryDay = dayjs().add(5, 'day'); // если ничего нет, то просто прибавляем 5 дней к сегодняшнему
    }
  } else {
    if (delivery.delivery_period_min) {
      closestDeliveryDay = dayjs().add(delivery.delivery_period_min + 1, 'day'); // для других доставок тоже добавляем 1 день к расчетному
    } else {
      closestDeliveryDay = dayjs().add(2, 'day');
    }
  }

  if (today.hour() > 19) closestDeliveryDay = closestDeliveryDay.add(1, 'day');

  // дата доставки не может быть раньше даты готовности подзаказного товара
  const minReadyDate = today.add(minSourceExtraShippingDays.value + 1, 'day');
  if (closestDeliveryDay.isBefore(minReadyDate, 'day')) {
    closestDeliveryDay = minReadyDate;
  }

  if (closestDeliveryDay.isSame(today, 'day')) {
    result = ' сегодня';
  } else if (closestDeliveryDay.isSame(today.add(1, 'day'), 'day')) {
    result = ' завтра';
  } else if (closestDeliveryDay.isSame(today.add(2, 'day'), 'day')) {
    result = ' послезавтра';
  } else {
    result = ` c&nbsp;${closestDeliveryDay.format(`D MMMM`)}`;
  }

  return result;
}

async function getAvailableDeliveries() {
  const requestId = ++deliveryRequestId;
  const offerId = selectedDeliveryOfferId.value;

  isDeliveryPricesLoaded.value = false;
  isLoading.value = true;
  deliveries.value = [];
  availableDeliveriesDetails.value = [];
  emit('update:is-free-delivery-available', false);

  if (!checkoutStore.selectedLocation) {
    isLoading.value = false;
    emit('update:is-free-delivery-available', false);

    return;
  }

  let nextAvailableDeliveriesDetails: DeliveryServiceDetailed[] = [];

  try {
    nextAvailableDeliveriesDetails = await checkoutStore.getDeliveryDetailsByOfferId(offerId);
  } catch (error) {
    if (error && import.meta.dev) {
      console.error(error);
    }
  }

  if (requestId !== deliveryRequestId || offerId !== selectedDeliveryOfferId.value) {
    return;
  }

  availableDeliveriesDetails.value = nextAvailableDeliveriesDetails;
  emit(
    'update:is-free-delivery-available',
    isFreeDeliveryAvailableFromDeliveryCalculation(availableDeliveriesDetails.value),
  );

  const yandexDetails = availableDeliveriesDetails.value.find(
    (d) => d?.slug === DELIVERY_SERVICES['express-mkad'].slug,
  );

  // если выбрана Москва
  if (yandexDetails && checkoutStore.selectedLocation.data.kladr_id === defaultLocations[0].data.kladr_id) {
    deliveries.value.push({
      title: `Экспресс доставка ${yandexDetails?.title}`,
      time: getDeliveryDates(
        minSourceExtraShippingDays.value > 0 ? minSourceExtraShippingDays.value : dayjs().get('hour') < 19 ? -1 : 0,
      ),
      price: yandexDetails?.delivery_price ?? 1000,
    });
  }

  const otherDeliveries = availableDeliveriesDetails.value.filter(
    (d) => d?.slug !== DELIVERY_SERVICES['express-mkad'].slug && d?.slug !== DELIVERY_SERVICES.russian_post.slug,
  );

  if (otherDeliveries?.length) {
    const price = Math.min(...otherDeliveries.map((delivery) => delivery.delivery_price ?? Infinity));
    const title = otherDeliveries.map((delivery) => DELIVERY_SERVICES[delivery.slug].title).join(', ') + ', курьер';

    const fastestOtherDelivery = getFastestDelivery(otherDeliveries);

    deliveries.value.push({
      price: price === Infinity ? 0 : price,
      time: fastestOtherDelivery ? calculateDeliveryTime(fastestOtherDelivery) : '',
      title,
    });
  }

  const russianPostDetails = availableDeliveriesDetails.value.find(
    (d) => d?.slug === DELIVERY_SERVICES.russian_post.slug,
  );

  if (russianPostDetails) {
    deliveries.value.push({
      title: russianPostDetails?.title,
      time: calculateDeliveryTime(russianPostDetails),
      price: russianPostDetails?.delivery_price ?? 0,
    });
  }

  isDeliveryPricesLoaded.value = true;
  isLoading.value = false;
}

const cheapestDeliveryTerms = computed(() => {
  const cheapestDelivery = availableDeliveriesDetails.value.reduce(
    (cheapest: DeliveryServiceDetailed, delivery: DeliveryServiceDetailed) => {
      if (
        !cheapest ||
        delivery.delivery_price < cheapest.delivery_price ||
        (delivery.delivery_price === cheapest.delivery_price &&
          delivery.delivery_period_min &&
          (cheapest.delivery_period_min === null || delivery.delivery_period_min < cheapest.delivery_period_min))
      ) {
        return delivery;
      }
      return cheapest;
    },
    null as never as DeliveryServiceDetailed,
  );

  return cheapestDelivery
    ? `${calculateDeliveryTime(cheapestDelivery)}${
        cheapestDelivery.delivery_price === 0 ? ', бесплатно' : ` от&nbsp;${cheapestDelivery.delivery_price}&nbsp;₽`
      }`
    : null;
});

watch(
  () => checkoutStore.selectedLocation,
  () => getAvailableDeliveries(),
);

watch(selectedDeliveryOfferId, () => getAvailableDeliveries());

watch(
  () => isShowDeliveryTermsModal.value,
  () => {
    if (isShowDeliveryTermsModal.value && !deliveries.value.length) getAvailableDeliveries();
  },
);

onMounted(async () => {
  await checkoutStore.getUserLocation();
  await getAvailableDeliveries();
});
</script>

<template>
  <section
    v-if="isShowSelfPickupBtn || isShowDeliveryBtn"
    class="product-page__availability-block"
  >
    <button
      v-if="isShowSelfPickupBtn"
      class="product-page__availability-block-btn"
      name="shops-available-to-pickup"
      @click="isShowShopsModal = true"
    >
      <SvgoPin
        :font-controlled="false"
        class="size-5"
        filled
      />

      <span
        v-if="offlineAvailableShopsCount > 0"
        class="text-left"
      >
        Самовывоз из
        <span class="product-page__availability-block-btn--highlight"> {{ offlineShopsCountPluralized }} </span>,
        {{ formatExtraDays(minOfflineExtraShippingDays) }}
      </span>

      <span
        v-else-if="shopsAvailableToPickup > 0"
        class="text-left"
      >
        Под заказ из
        <span class="product-page__availability-block-btn--highlight">{{ shopsAvailableToPickupPluralized }} </span>,
        {{ formatExtraDays(minSourceExtraShippingDays + 1) }}
      </span>
    </button>

    <button
      v-if="isShowDeliveryBtn"
      class="product-page__availability-block-btn"
      name="delivery-terms"
      @click="isShowDeliveryTermsModal = true"
    >
      <SvgoShipment
        :font-controlled="false"
        class="size-5"
        filled
      />
      <span
        v-if="checkoutStore.selectedLocation && cheapestDeliveryTerms && isDeliveryPricesLoaded"
        class="text-left leading-normal"
      >
        В
        <span class="product-page__availability-block-btn--highlight">
          {{
            checkoutStore.selectedLocation.data.city_with_type ??
            checkoutStore.selectedLocation.data.settlement_with_type
          }}
        </span>

        <span v-html="cheapestDeliveryTerms" />
      </span>

      <span
        v-else
        class="text-left"
      >
        Узнать условия
        <span class="product-page__availability-block-btn--highlight"> доставки </span>

        <Transition
          v-if="isLoading"
          name="loader"
        >
          <SvgoSpinner
            :font-controlled="false"
            class="animate-spin inline-block ml-1 size-3"
            title="Идет загрузка..."
          />
        </Transition>
      </span>
    </button>

    <ProductPageAvailabilityInShopsModal v-model="isShowShopsModal" />

    <ProductPageAvailableDeliveryTermsModal
      v-model="isShowDeliveryTermsModal"
      :deliveries
      :isLoading
    />
  </section>
</template>

<style lang="scss" scoped>
.product-page {
  &__availability-block {
    display: flex;
    flex-direction: column;
  }

  &__availability-block-btn {
    display: flex;
    gap: 8px;
    justify-content: flex-start;
    align-items: center;
    overflow: hidden;
    padding: 12px;
    border-top: 1px solid $black-10;
    border-right: 1px solid $black-10;
    border-left: 1px solid $black-10;
    background-color: $white;
    font-weight: $bold;
    font-size: $text-sm;

    &:first-of-type {
      border-top: 1px solid $black-10;
      border-bottom: none;
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
    }

    &:last-of-type {
      border-bottom: 1px solid $black-10;
      border-bottom-right-radius: 12px;
      border-bottom-left-radius: 12px;
    }

    &--highlight {
      color: $blue;
      transition: all 0.3s;
    }

    &:hover {
      .product-page__availability-block-btn--highlight {
        color: $label-violet;
      }
    }
  }
}
</style>
